import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HandCoins, Store } from "lucide-react";
import { Box, Typography } from "@mui/material";
import Loading from "../../components/Loading";
import useFetch from "../../hooks/useFetch";
import { signup_url } from "../../api/urls";
import { useSelector } from "react-redux";

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  hover: { scale: 1.03, transition: { duration: 0.3 } },
  tap: { scale: 0.97 },
};

const Portal = () => {
  const { accessToken } = useSelector((state) => state.auth);
  const { data: user } = useFetch(accessToken && signup_url);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#f0f2f5",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <Loading />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
        bgcolor: "#f0f2f5",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 3, md: 5 },
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Profile Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          maxWidth: 1400,
          width: "100%",
          bgcolor: "white",
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
        }}
      >
        <Box
          component="img"
          src={user?.profile_image}
          alt="Profile"
          sx={{
            width: { xs: 30, sm: 40, md: 50, lg: 60 },
            height: { xs: 30, sm: 40, md: 50, lg: 60 },
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <Box>
          <Typography sx={{ fontSize: "18px" }}>{user?.full_name}</Typography>
        </Box>
      </Box>

      {/* Cards Section */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: { xs: 2, sm: 3 },
          maxWidth: 1400,
          width: "100%",
        }}
      >
        {/* Wholesale Store Card */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          whileTap="tap"
          onClick={() => navigate("/b2b_store")}
          style={{
            backgroundColor: "white",
            borderRadius: 16,
            cursor: "pointer",
            display: "flex",
            gap: 20,
            padding: 24,
            alignItems: "center",
            minHeight: 160,
          }}
        >
          <Box
            sx={{
              color: "#000000",
              borderRadius: "50%",
              width: 72,
              height: 72,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // bgcolor: "#e3f2fd"
            }}
          >
            <Store size={38} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Wholesale Store
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              A wholesale store focuses on fulfilling the unique needs of
              corporate clients, offering bulk purchasing options, negotiated
              pricing, and tailored services.
            </Typography>
          </Box>
        </motion.div>

        {/* B2B Credit Card */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          whileTap="tap"
          onClick={() => navigate("/b2b_credit")}
          style={{
            backgroundColor: "white",
            borderRadius: 16,
            cursor: "pointer",
            display: "flex",
            gap: 20,
            padding: 24,
            alignItems: "center",
            minHeight: 160,
          }}
        >
          <Box
            sx={{
              color: "#000000", // changed icon color to black
              borderRadius: "50%",
              width: 72,
              height: 72,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // bgcolor: "#e8f5e9",
            }}
          >
            <HandCoins size={38} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              B2B Credit
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              A B2B store focuses on fulfilling the unique needs of corporate
              clients, offering bulk purchasing options, negotiated pricing, and
              tailored services.
            </Typography>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Portal;
